// src/ui/screens/chats/widgets/TopBar.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SelectionTopBar } from './SelectionTopBar';

const COLORS = {
  orange: '#FF8C08',
  card: '#23262C',
};

// ── TopBar ────────────────────────────────────────────────────────────────────

export const TopBar = ({
  isSelectionMode = false,
  selectedCount = 0,
  onEditPressed = null,
  onSelectionClose = null,
  isPinned = false,
  isMuted = false,
  isArchived = false,
  isBlocked = false,
  onMarkAsRead = null,
  onPin = null,
  onBlock = null,
  onMute = null,
  onArchive = null,
  onDelete = null,
  onStoriesPressed = null,
  onMorePressed = null,
}) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const menuButtonRef = useRef(null);
  const menuRef = useRef(null);

  // ── Handle menu toggle ────────────────────────────────────────────────────

  const handleToggleMenu = () => {
    setShowMenu(!showMenu);
  };

  // ── Handle menu item selection ───────────────────────────────────────────

  const handleMenuItem = (value) => {
    setShowMenu(false);
    if (value === 'contact') {
      navigate('/new-contact');
    } else if (value === 'profile') {
      navigate('/profile');
    } else if (value === 'add-property') {
      navigate('/add-property');
    }
  };

  // ── Close menu on outside click ──────────────────────────────────────────

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          menuButtonRef.current && !menuButtonRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── If in selection mode, render SelectionTopBar ─────────────────────────

  if (isSelectionMode) {
    return (
      <SelectionTopBar
        selectedCount={selectedCount}
        onClose={onSelectionClose || (() => {})}
        onMarkAsRead={onMarkAsRead || (() => {})}
        onPin={onPin || (() => {})}
        isPinned={isPinned}
        onBlock={onBlock || (() => {})}
        isBlocked={isBlocked}
        onMute={onMute || (() => {})}
        isMuted={isMuted}
        onArchive={onArchive || (() => {})}
        isArchived={isArchived}
        onDelete={onDelete || (() => {})}
      />
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  // NOTE: This is intentionally a normal in-flow block (no `fixed`/`absolute`
  // here). The parent (ConversationsScreen) already wraps TopBar, SearchBar,
  // and TabBarSection in a single `absolute top-0` header container and
  // stacks them via normal block flow — exactly like Flutter's `Column`
  // inside the `Positioned` sticky-header layer. If TopBar is `fixed`, it
  // detaches from that stack and overlaps its siblings (the bug you saw).

  return (
    <div className="relative z-40 px-4" style={{ paddingTop: 12 }}>
      <div className="flex items-center justify-between">
        {/* Edit Button */}
        <button
          onClick={onEditPressed}
          className="h-11 rounded-full flex items-center justify-center"
          style={{
            paddingLeft: 18,
            paddingRight: 18,
            backgroundColor: 'rgba(35,38,44,0.3)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid #23262C',
          }}
        >
          <span className="text-filatom-primary text-sm font-medium">Edit</span>
        </button>

        {/* Icons Pill */}
        <div
          className="h-11 rounded-full flex items-center overflow-hidden"
          style={{
            backgroundColor: 'rgba(35,38,44,0.3)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid #23262C',
          }}
        >
          {/* Add Property Button */}
          <button
            onClick={() => navigate('/post/add-property')}
            className="w-11 h-11 flex items-center justify-center"
          >
            <svg
              style={{ width: 24, height: 24 }}
              className="text-filatom-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          {/* More Button */}
          <div className="relative">
            <button
              ref={menuButtonRef}
              onClick={handleToggleMenu}
              className="w-11 h-11 flex items-center justify-center"
            >
              <svg
                style={{ width: 22, height: 22 }}
                className="text-filatom-primary"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="5" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="12" cy="19" r="2" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showMenu && (
              <div
                ref={menuRef}
                className="absolute top-11 right-0 min-w-[180px] rounded-xl shadow-lg py-1 z-50"
                style={{
                  backgroundColor: 'rgba(35,38,44,0.3)',
                  backdropFilter: 'blur(28px)',
                  WebkitBackdropFilter: 'blur(28px)',
                  border: '1px solid #23262C',
                }}
              >
                <button
                  onClick={() => handleMenuItem('contact')}
                  className="w-full flex items-center gap-3.5 px-4 py-3 hover:bg-white/5 transition-colors"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  <span className="text-white text-sm font-medium">New contact</span>
                </button>

                <div className="mx-4 h-px bg-white/8" />

                <button
                  onClick={() => handleMenuItem('profile')}
                  className="w-full flex items-center gap-3.5 px-4 py-3 hover:bg-white/5 transition-colors"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-white text-sm font-medium">Profile</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;